import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { getCompany, getMarketHistory } from "@/lib/economy-api";
import { formatGros } from "@/lib/currency";
import { AccountShell } from "@/components/account/AccountShell";
import { OwnershipPieChart } from "@/components/economy/OwnershipPieChart";
import { MarketHistoryChart } from "@/components/economy/MarketHistoryChart";
import { MarketAdjustDialog } from "@/components/economy/MarketAdjustDialog";
import { CoinAmount } from "@/components/economy/CoinAmount";
import { COMPANY_DETAIL } from "@/constants/testIds";

// Which key inside a history entry's old_value/new_value JSON holds the groš
// amount to render as coins, per event type - value_changed/market_adjusted
// both move total_value_gros, profit_changed moves weekly_profit_base_gros.
const HISTORY_VALUE_KEY = {
  value_changed: "total_value_gros",
  market_adjusted: "total_value_gros",
  profit_changed: "weekly_profit_base_gros",
};

const HISTORY_LABELS = {
  created: "Založení",
  value_changed: "Změna hodnoty",
  profit_changed: "Změna zisku",
  activated: "Aktivace",
  deactivated: "Deaktivace",
  deleted: "Smazání",
  share_granted: "Přidělení podílu",
  share_revoked: "Odebrání podílu",
  share_transferred: "Převod podílu",
  share_purchased: "Nákup podílu",
  payout: "Výplata",
  market_adjusted: "Zásah GM do trhu",
};

// Simulation parameters (economy/market_service.py FLOOR_RATIO/CEILING_RATIO) -
// duplicated here only for chart reference lines, not for any actual math.
const FLOOR_RATIO = 0.2;
const CEILING_RATIO = 3.0;

export const CompanyDetailPage = () => {
  const { user, loading } = useAuth();
  const { companyId } = useParams();
  const [company, setCompany] = useState(undefined);
  const [marketHistory, setMarketHistory] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const refresh = useCallback(() => {
    Promise.all([getCompany(companyId), getMarketHistory(companyId)])
      .then(([c, history]) => {
        setCompany(c);
        setMarketHistory(history);
      })
      .catch(() => setCompany(null));
  }, [companyId]);

  useEffect(() => {
    if (!user) return;
    refresh();
  }, [user, refresh]);

  if (loading) {
    return (
      <section className="min-h-[70vh] px-6 pb-24 pt-40 text-center">
        <p className="font-display text-sm uppercase tracking-[0.2em] text-white/40">Načítám…</p>
      </section>
    );
  }

  if (!user) return <Navigate to="/ucet" replace />;

  if (company === null) {
    return (
      <AccountShell isGm={user.is_gm} testId={COMPANY_DETAIL.notFound}>
        <div className="text-center">
          <p className="text-sm text-white/50">Tenhle podnik neexistuje nebo byl smazán.</p>
          <Link to="/ekonomika" className="mt-4 inline-block text-sm text-[#F0801F] hover:underline">
            Zpět na Moje investice
          </Link>
        </div>
      </AccountShell>
    );
  }

  if (company === undefined) {
    return (
      <section className="min-h-[70vh] px-6 pb-24 pt-40 text-center">
        <p className="font-display text-sm uppercase tracking-[0.2em] text-white/40">Načítám…</p>
      </section>
    );
  }

  const baseValue = Number(company.base_value_gros);

  return (
    <AccountShell isGm={user.is_gm} testId={COMPANY_DETAIL.page}>
      <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
        Horowitzova městská banka - podnik
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
          {company.name}
        </h1>
        {!company.is_active && (
          <span className="rounded-full bg-white/10 px-3 py-1 text-xs uppercase text-white/40">Neaktivní</span>
        )}
        {user.is_gm && (
          <button
            onClick={() => setAdjustOpen(true)}
            data-testid={COMPANY_DETAIL.adjustMarketButton}
            className="ml-auto rounded-md border border-[#F0801F]/40 px-4 py-2 text-xs uppercase tracking-wide text-[#F0801F] hover:bg-[#F0801F]/10"
          >
            Upravit trh
          </button>
        )}
      </div>
      {company.description && (
        <p className="mt-4 max-w-2xl whitespace-pre-line text-white/60">{company.description}</p>
      )}

      {company.image_url && (
        <img
          src={company.image_url}
          alt={company.name}
          className="mt-6 max-h-80 w-full rounded-xl border border-white/10 object-cover"
        />
      )}

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-white/10 bg-[#17171b] p-6">
          <p className="text-xs uppercase tracking-wide text-white/40">Aktuální hodnota na trhu</p>
          <CoinAmount amount={company.total_value_gros} size="md" className="mt-2.5" />
        </div>
        <div className="rounded-xl border border-white/10 bg-[#17171b] p-6">
          <p className="text-xs uppercase tracking-wide text-white/40">Cena za 1 % podílu</p>
          <CoinAmount amount={company.price_per_share_gros} size="md" className="mt-2.5" />
        </div>
        <div className="rounded-xl border border-white/10 bg-[#17171b] p-6">
          <p className="text-xs uppercase tracking-wide text-white/40">Týdenní čistý zisk</p>
          <CoinAmount amount={company.weekly_profit_effective_gros} size="md" className="mt-2.5" />
        </div>
      </div>

      <p className="mt-4 text-sm text-white/50">
        NPC vlastník: {company.npc_owner_name} · K prodeji hráčům {company.percent_for_sale} % (aktuálně volných{" "}
        {company.available_for_sale} %)
      </p>

      <div className="mt-10">
        <h2 className="mb-4 font-display text-lg font-600 uppercase tracking-tight text-white">Vývoj na trhu</h2>
        <div className="rounded-xl border border-white/10 bg-[#17171b] p-6" data-testid={COMPANY_DETAIL.marketChart}>
          <MarketHistoryChart
            history={marketHistory}
            baseValueGros={baseValue}
            floorGros={baseValue * FLOOR_RATIO}
            ceilingGros={baseValue * CEILING_RATIO}
          />
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <h2 className="mb-4 font-display text-lg font-600 uppercase tracking-tight text-white">Vlastníci</h2>
          <div className="rounded-xl border border-white/10 bg-[#17171b] p-6" data-testid={COMPANY_DETAIL.ownershipChart}>
            <OwnershipPieChart
              shareholders={company.shareholders}
              npcOwnerName={company.npc_owner_name}
              unassignedPercent={100 - company.shares_assigned}
            />
          </div>
          <div className="mt-3 space-y-2">
            {company.shareholders.map((s) => (
              <div
                key={s.character_guid}
                data-testid={COMPANY_DETAIL.shareholderRow}
                className="flex items-center justify-between rounded-md border border-white/10 px-4 py-3 text-sm"
              >
                <span className="text-white/80">{s.character_name}</span>
                <span className="flex items-center gap-2">
                  {s.is_majority_owner && (
                    <span className="rounded-full bg-[#F0801F]/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#F0801F]">
                      Většinový vlastník
                    </span>
                  )}
                  <span className="text-[#F0801F]">{s.shares} %</span>
                </span>
              </div>
            ))}
          </div>

          <h2 className="mb-4 mt-10 font-display text-lg font-600 uppercase tracking-tight text-white">
            Historie výplat
          </h2>
          <div className="space-y-2">
            {company.payouts.length === 0 && <p className="text-sm text-white/50">Zatím žádné výplaty.</p>}
            {company.payouts.map((p, i) => (
              <div
                key={i}
                data-testid={COMPANY_DETAIL.payoutItem}
                className="rounded-md border border-white/10 px-4 py-3 text-sm text-white/70"
              >
                <div className="flex justify-between">
                  <span>{new Date(p.period_start).toLocaleDateString("cs-CZ")}</span>
                  <span>{formatGros(p.total_credited_gros)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="mb-4 font-display text-lg font-600 uppercase tracking-tight text-white">Historie</h2>
          <div className="space-y-2">
            {company.history.length === 0 && <p className="text-sm text-white/50">Zatím žádné záznamy.</p>}
            {company.history.map((h, i) => {
              const valueKey = HISTORY_VALUE_KEY[h.event_type];
              const oldAmount = valueKey ? h.old_value?.[valueKey] : null;
              const newAmount = valueKey ? h.new_value?.[valueKey] : null;
              return (
                <div
                  key={i}
                  data-testid={COMPANY_DETAIL.historyItem}
                  className="rounded-md border border-white/10 px-4 py-3 text-sm"
                >
                  <div className="flex items-center justify-between text-xs text-white/40">
                    <span>{HISTORY_LABELS[h.event_type] ?? h.event_type}</span>
                    <span>{new Date(h.created_at).toLocaleString("cs-CZ")}</span>
                  </div>
                  {oldAmount != null && newAmount != null ? (
                    <div className="mt-2 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="w-8 shrink-0 text-xs text-white/40">Z</span>
                        <CoinAmount amount={oldAmount} size="md" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-8 shrink-0 text-xs text-white/40">Na</span>
                        <CoinAmount amount={newAmount} size="md" />
                      </div>
                      {h.new_value?.reason && (
                        <p className="text-white/70">Důvod: {h.new_value.reason}</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 text-white/70">{h.description}</p>
                  )}
                  <p className="mt-1 text-xs text-white/30">{h.actor_username ?? "Systém"}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {adjustOpen && (
        <MarketAdjustDialog
          open
          company={company}
          onClose={() => setAdjustOpen(false)}
          onAdjusted={() => {
            setAdjustOpen(false);
            refresh();
          }}
        />
      )}
    </AccountShell>
  );
};
